import { useTranslation } from "react-i18next";
import { useId } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";
import { TONE_VAR, type TWidgetTone, useAnimationsEnabled } from "./tone";

/**
 * Shapes: rings, bars and tracks that draw a proportion.
 *
 * Part of the widget primitive vocabulary — see ./index.ts. Widgets differ in
 * what they measure, not in how a number, a chart or a person-row looks, so
 * every shape lives here rather than being re-invented per widget.
 */

/**
 * Share-of-total ring.
 *
 * An SVG rather than a chart library: it is one arc, `stroke-dasharray` keeps it
 * crisp at every widget size, and it costs nothing in the bundle. The arc sweeps
 * in from empty so the proportion is read as a proportion rather than as a
 * decorative circle that happens to be part-coloured.
 */
export const MetricRing = ({
  percent,
  label,
  tone = "primary",
  size = 64,
  caption,
  isLoading,
}: {
  percent: number;
  label: string;
  tone?: TWidgetTone;
  size?: number;
  caption?: string;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation("company");
  const animate = useAnimationsEnabled();
  // Unique per instance: several rings can be on screen together, and SVG
  // gradient ids collide across the whole document, not just within one ring.
  const gradientId = `metric-ring-gradient-${useId()}`;

  const stroke = size >= 72 ? 8 : 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, percent));
  const toneVar = `var(${TONE_VAR[tone]})`;

  if (isLoading) {
    return (
      <Skeleton
        className="rounded-full"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={t("company:widgets.label_percent", {
        label,
        percent: Math.round(clamped),
      })}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <defs>
          {/* A light-to-full sweep along the tone rather than one flat colour —
              the arc reads as lit from one end rather than painted. */}
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop
              offset="0%"
              style={{
                stopColor: `color-mix(in oklab, ${toneVar} 55%, white)`,
              }}
            />
            <stop offset="100%" style={{ stopColor: toneVar }} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className="stroke-muted"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped / 100)}
          stroke={`url(#${gradientId})`}
          style={{ ["--ring-circumference" as string]: `${circumference}` }}
          className={cn(
            animate && "widget-ring-arc",
            "transition-[stroke-dashoffset] duration-500",
          )}
        />
      </svg>
      {/* No number in the ring itself — the arc is the figure, and every
          caller already states the count in plain text beside or below it
          (`present/total`, a MiniStat). A second, smaller rendering of the
          same fact inside the ring was the thing overflowing at small sizes;
          removing it is simpler than shrinking it further. `caption` still
          renders here since it's the one thing the arc can't say on its own. */}
      {caption && (
        <span className="absolute inset-0 flex items-center justify-center leading-none">
          <span className="text-[9px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
            {caption}
          </span>
        </span>
      )}
    </div>
  );
};

/** Proportional composition bar — the flat alternative to a ring for 3+ parts. */
export const CompositionBar = ({
  segments,
  isLoading,
}: {
  segments: { label: string; value: number; tone: TWidgetTone }[];
  isLoading?: boolean;
}) => {
  const { t } = useTranslation("company");
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  if (isLoading) return <Skeleton className="h-2 w-full rounded-full" />;

  return (
    <div
      className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-muted"
      role="img"
      aria-label={segments
        .map((segment) =>
          t("company:widgets.label_colon_value", {
            label: segment.label,
            value: segment.value,
          }),
        )
        .join(", ")}
    >
      {total > 0 &&
        segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <span
              key={segment.label}
              className="rounded-full transition-[width] duration-700"
              style={{
                width: `${(segment.value / total) * 100}%`,
                // A lit gradient, not a flat fill — matches the ring and the
                // progress track so every proportion shape in the vocabulary
                // reads as the same lit material.
                background: `linear-gradient(to top, color-mix(in oklab, var(${TONE_VAR[segment.tone]}) 65%, transparent), var(${TONE_VAR[segment.tone]}))`,
              }}
            />
          ))}
    </div>
  );
};

/**
 * A bare series of bars — no axes, no gridlines, no legend.
 *
 * At widget scale a labelled chart is unreadable and a shape is not: the useful
 * question a 40px-tall series answers is "is this rising, falling or flat", and
 * bars answer it without any chrome. The tallest bar carries the tone so the peak
 * is findable at a glance.
 */
export const Sparkbars = ({
  series,
  tone = "primary",
  isLoading,
  className,
}: {
  series: { label: string; value: number }[];
  tone?: TWidgetTone;
  isLoading?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation(["common", "company"]);
  const animate = useAnimationsEnabled();

  if (isLoading) {
    return <Skeleton className={cn("h-full w-full rounded-sm", className)} />;
  }

  if (series.length === 0) {
    return (
      <p
        className={cn(
          "flex items-center text-[11px] text-muted-foreground",
          className,
        )}
      >
        {t("company:widgets.no_history_yet")}
      </p>
    );
  }

  const peak = Math.max(...series.map((point) => point.value), 1);

  return (
    <div
      className={cn("flex h-full items-end gap-1", className)}
      role="img"
      aria-label={series
        .map((point) =>
          t("company:widgets.label_colon_value", {
            label: point.label,
            value: point.value,
          }),
        )
        .join(", ")}
    >
      {series.map((point, index) => {
        const isPeak = point.value === peak;
        return (
          <span
            key={`${point.label}-${index}`}
            className="flex h-full min-w-0 flex-1 flex-col justify-end gap-1"
          >
            <span
              className={cn(
                "w-full rounded-t-[3px]",
                !isPeak && "bg-muted",
                animate && "widget-bar",
              )}
              style={{
                height: `${Math.max((point.value / peak) * 100, 4)}%`,
                animationDelay: `${index * 45}ms`,
                // A lit gradient, not a flat fill — matches the ring's sweep.
                // Only the peak bar earns it; every bar tinted would compete
                // with itself for which one is actually the point.
                ...(isPeak && {
                  background: `linear-gradient(to top, color-mix(in oklab, var(${TONE_VAR[tone]}) 65%, transparent), var(${TONE_VAR[tone]}))`,
                }),
              }}
            />
            <span className="truncate text-center text-[9px] leading-none text-muted-foreground">
              {point.label}
            </span>
          </span>
        );
      })}
    </div>
  );
};

/**
 * A fraction of a whole, drawn full-width.
 *
 * `CompositionBar` above splits one bar across several categories; this shows a
 * single value against its own total, which is the more widget-shaped question
 * — "42 of 50 in", "18 of 60 free". The figures sit on the track's own line
 * rather than above it, so the whole thing costs one row instead of two, and the
 * bar runs the full width of the body instead of being inset in a text column.
 */
export const ProgressTrack = ({
  value,
  total,
  label,
  tone = "primary",
  isLoading,
}: {
  value: number;
  total: number;
  label: string;
  tone?: TWidgetTone;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation("company");
  const animate = useAnimationsEnabled();
  // Guard the divide: a company with no assets tracked, or no employees yet,
  // would otherwise render NaN% as a bar width.
  const percent = total > 0 ? Math.min(100, (value / total) * 100) : 0;

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <Skeleton className="h-2.5 w-24 rounded" />
        <Skeleton className="h-1.5 w-full rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-[10px] font-medium tracking-[0.05em] text-muted-foreground uppercase">
          {label}
        </span>
        <span className="shrink-0 text-[11px] font-semibold tabular-nums text-foreground">
          {value.toLocaleString()}
          <span className="text-muted-foreground">
            /{total.toLocaleString()}
          </span>
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label={t("company:widgets.label_value_of_total", {
          label,
          value,
          total,
        })}
      >
        <span
          className={cn(
            "block h-full rounded-full",
            animate && "transition-[width] duration-700",
          )}
          style={{
            width: `${percent}%`,
            background: `linear-gradient(to right, color-mix(in oklab, var(${TONE_VAR[tone]}) 65%, transparent), var(${TONE_VAR[tone]}))`,
          }}
        />
      </div>
    </div>
  );
};
